import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import Modal from '../../components/Modal';
import Pagination from '../../components/common/Pagination';
import { fetchAllNotices, fetchNoticeDetail, createNotice, updateNotice, deleteNotice } from '../../services/api';
import { useTranslation } from '../../context/LanguageContext';
import ManagementHeader from '../management/ManagementHeader';
import MgField from '../management/MgField';
import '../management/management.css';

const DASH = '—';
/* management.css 의 .mg-badge 색 이름 */
const PRIORITY_BADGE = { high: 'error', normal: 'warning', low: 'success' };
const NOTICE_STATUS_BADGE = { active: 'success', waiting: 'warning', scheduled: 'info', inactive: 'neutral', ended: 'neutral' };

function NoticeManagementPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [noticeToDelete, setNoticeToDelete] = useState(null);
  const [notices, setNotices] = useState([]);
  const [pendingOpenNoticeId, setPendingOpenNoticeId] = useState(null);

  // 페이지네이션 상태
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);

  // 공지사항 목록 로드
  useEffect(() => {
    // 헤더에서 넘어온 공지사항 ID 저장
    if (location.state?.openNoticeId) {
      setPendingOpenNoticeId(location.state.openNoticeId);
    }
    loadNotices();
  }, [location.state]);

  const getNoticeStatusKey = (notice) => {
    const now = new Date();
    const { startDate, endDate, isActive, isPinned } = notice;

    if (!isActive) return 'inactive';
    if (endDate && new Date(endDate) < now) return 'ended';
    if (startDate && new Date(startDate) > now) return 'scheduled';
    if (isActive && isPinned) return 'active';
    return 'waiting';
  };

  const loadNotices = async () => {
    try {
      const data = await fetchAllNotices();
      // API 데이터를 프론트 형식으로 변환
      const formattedNotices = data.map(notice => {
        const noticeData = {
          id: notice.id,
          title: notice.title,
          content: notice.content || '',
          category: notice.category || t('pages.user.notice.seed.defaultCategory'),
          author: t('pages.user.notice.seed.defaultAuthor'),
          views: notice.viewCount || 0,
          createdAt: notice.createdAt,
          priorityKey: notice.type === 'urgent' ? 'high' : notice.type === 'update' ? 'normal' : 'normal',
          isPinned: notice.isNotice || false,
          isActive: notice.isActive !== false,
          startDate: notice.startDate,
          endDate: notice.endDate,
          noticeType: notice.type || 'notice'
        };
        noticeData.statusKey = getNoticeStatusKey(noticeData);
        return noticeData;
      });
      setNotices(formattedNotices);
    } catch (error) {
      console.error('공지사항 로드 실패:', error);
      setNotices([]);
    }
  };

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [newNotice, setNewNotice] = useState({
    title: '',
    content: '',
    category: 'general',
    priorityKey: 'normal',
    isPinned: false,
    startDate: '',
    endDate: ''
  });
  const [editNotice, setEditNotice] = useState({
    id: null,
    title: '',
    content: '',
    category: 'general',
    priorityKey: 'normal',
    isPinned: false,
    startDate: '',
    endDate: ''
  });

  // 헤더에서 클릭한 공지사항 모달 열기
  useEffect(() => {
    if (pendingOpenNoticeId && notices.length > 0) {
      const noticeToOpen = notices.find(n => n.id === pendingOpenNoticeId);
      if (noticeToOpen) {
        handleNoticeClick(noticeToOpen);
        setPendingOpenNoticeId(null);
      }
    }
  }, [pendingOpenNoticeId, notices]);

  const handleNoticeClick = async (notice) => {
    try {
      await fetchNoticeDetail(notice.id);
      setNotices(notices.map(n =>
        n.id === notice.id ? { ...n, views: n.views + 1 } : n
      ));
    } catch (error) {
      console.error('조회수 증가 실패:', error);
    }
    handleOpenEditModal(notice);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteNotice(noticeToDelete.id);
      alert(t('pages.user.notice.alertDeleted', { title: noticeToDelete.title }));
      setNotices(notices.filter(n => n.id !== noticeToDelete.id));
      setIsDeleteModalOpen(false);
      setNoticeToDelete(null);
    } catch (error) {
      console.error('공지 삭제 실패:', error);
      alert(t('pages.user.notice.alertDeleteFail'));
    }
  };

  /* 삭제 확인창 열기 — 목록의 휴지통 버튼과 수정 팝업의 삭제 버튼이 같이 쓴다. DELETE /notices/{id} 는 비활성 처리(목록에서 빠짐) */
  const handleDeleteClick = (notice) => {
    setNoticeToDelete(notice);
    setIsDeleteModalOpen(true);
  };

  const handleOpenAddModal = () => {
    setIsAddModalOpen(true);
    setNewNotice({
      title: '',
      content: '',
      category: 'general',
      priorityKey: 'normal',
      isPinned: false,
      startDate: '',
      endDate: ''
    });
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
    setNewNotice({
      title: '',
      content: '',
      category: 'general',
      priorityKey: 'normal',
      isPinned: false,
      startDate: '',
      endDate: ''
    });
  };

  const handleInputChange = (field, value) => {
    setNewNotice(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditInputChange = (field, value) => {
    setEditNotice(prev => ({
      ...prev,
      [field]: value
    }));
  };

  /* datetime-local 입력값(현지 시각). toISOString 은 UTC 라 한국에서는 9시간 이르게 들어갔다 */
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  };

  // category code <-> backend mapping (we keep code keys; backend stores the localized value)
  const categoryKeys = ['system', 'equipment', 'quality', 'alert', 'education', 'general'];

  const handleOpenEditModal = (notice) => {
    setEditNotice({
      id: notice.id,
      title: notice.title,
      content: notice.content,
      category: notice.category,
      priorityKey: notice.priorityKey || 'normal',
      isPinned: notice.isPinned,
      startDate: formatDateForInput(notice.startDate),
      endDate: formatDateForInput(notice.endDate)
    });
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditNotice({
      id: null,
      title: '',
      content: '',
      category: 'general',
      priorityKey: 'normal',
      isPinned: false,
      startDate: '',
      endDate: ''
    });
  };

  const priorityToType = (priorityKey) => {
    if (priorityKey === 'high') return 'urgent';
    if (priorityKey === 'normal') return 'update';
    return 'notice';
  };

  const priorityToNumber = (priorityKey) => {
    if (priorityKey === 'high') return 2;
    if (priorityKey === 'normal') return 1;
    return 0;
  };

  const handleUpdateNotice = async () => {
    if (!editNotice.title.trim()) {
      alert(t('pages.user.notice.alertTitleRequired'));
      return;
    }

    if (!editNotice.content.trim()) {
      alert(t('pages.user.notice.alertContentRequired'));
      return;
    }

    if (editNotice.startDate && editNotice.endDate) {
      if (new Date(editNotice.endDate) < new Date(editNotice.startDate)) {
        alert(t('pages.user.notice.alertEndAfterStart'));
        return;
      }
    }

    try {
      const requestData = {
        title: editNotice.title,
        content: editNotice.content,
        category: editNotice.category,
        noticeType: priorityToType(editNotice.priorityKey),
        isNotice: editNotice.isPinned,
        priority: priorityToNumber(editNotice.priorityKey),
        startDate: editNotice.startDate || null,
        endDate: editNotice.endDate || null
      };

      await updateNotice(editNotice.id, requestData);
      handleCloseEditModal();
      alert(t('pages.user.notice.alertUpdateSuccess'));
      loadNotices();
    } catch (error) {
      console.error('공지사항 수정 실패:', error);
      alert(t('pages.user.notice.alertUpdateFail'));
    }
  };

  const handleAddNotice = async () => {
    if (!newNotice.title.trim()) {
      alert(t('pages.user.notice.alertTitleRequired'));
      return;
    }

    if (!newNotice.content.trim()) {
      alert(t('pages.user.notice.alertContentRequired'));
      return;
    }

    if (newNotice.startDate && newNotice.endDate) {
      if (new Date(newNotice.endDate) < new Date(newNotice.startDate)) {
        alert(t('pages.user.notice.alertEndAfterStart'));
        return;
      }
    }

    try {
      const requestData = {
        title: newNotice.title,
        content: newNotice.content,
        category: newNotice.category,
        noticeType: priorityToType(newNotice.priorityKey),
        isNotice: newNotice.isPinned,
        isPush: false,
        isActive: true,
        priority: priorityToNumber(newNotice.priorityKey),
        startDate: newNotice.startDate || null,
        endDate: newNotice.endDate || null
      };

      await createNotice(requestData);
      handleCloseAddModal();
      alert(t('pages.user.notice.alertCreateSuccess'));
      loadNotices();
    } catch (error) {
      console.error('공지사항 등록 실패:', error);
      alert(t('pages.user.notice.alertCreateFail'));
    }
  };

  const filteredNotices = useMemo(() => {
    return notices
      .filter(notice => {
        const categoryMatch = filterCategory === 'all' || notice.category === filterCategory;
        const statusMatch = filterStatus === 'all' || notice.statusKey === filterStatus;
        const searchMatch = searchKeyword === '' ||
          notice.title.toLowerCase().includes(searchKeyword.toLowerCase()) ||
          notice.content.toLowerCase().includes(searchKeyword.toLowerCase());
        return categoryMatch && statusMatch && searchMatch;
      })
      .sort((a, b) => {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateB - dateA;
      });
  }, [notices, filterCategory, filterStatus, searchKeyword]);

  // 페이지네이션 적용
  const paginatedNotices = useMemo(() => {
    const startIndex = page * size;
    return filteredNotices.slice(startIndex, startIndex + size);
  }, [filteredNotices, page, size]);

  const totalElements = filteredNotices.length;
  const totalPages = Math.ceil(totalElements / size);

  const handleSizeChange = (newSize) => {
    setSize(newSize);
    setPage(0);
  };

  // 필터 변경 시 첫 페이지로
  useEffect(() => {
    setPage(0);
  }, [filterCategory, filterStatus, searchKeyword]);

  const localizeCategory = (cat) => {
    if (categoryKeys.includes(cat)) {
      return t(`pages.user.notice.filter.categories.${cat}`);
    }
    return cat;
  };

  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('ko-KR') : null);
  const dateCell = (d) => (d ? fmtDate(d) : <span className="mg-muted">{DASH}</span>);

  /* 등록·수정 팝업이 같이 쓰는 입력 폼. values 는 newNotice/editNotice, onChange(field, value) */
  const renderNoticeForm = (values, onChange) => (
    <>
      <MgField label={t('pages.user.notice.form.titleLabel')} required>
        <input type="text" className="mg-input" placeholder={t('pages.user.notice.form.titlePlaceholder')} value={values.title} onChange={(e) => onChange('title', e.target.value)} />
      </MgField>
      <div className="mg-grid">
        <MgField label={t('pages.user.notice.form.categoryLabel')}>
          <select className="mg-select" value={values.category} onChange={(e) => onChange('category', e.target.value)}>
            {categoryKeys.map((k) => (
              <option key={k} value={k}>{t(`pages.user.notice.filter.categories.${k}`)}</option>
            ))}
          </select>
        </MgField>
        <MgField label={t('pages.user.notice.form.priorityLabel')}>
          <select className="mg-select" value={values.priorityKey} onChange={(e) => onChange('priorityKey', e.target.value)}>
            <option value="low">{t('pages.user.notice.priority.low')}</option>
            <option value="normal">{t('pages.user.notice.priority.normal')}</option>
            <option value="high">{t('pages.user.notice.priority.high')}</option>
          </select>
        </MgField>
        <MgField label={t('pages.user.notice.form.startDateLabel')}>
          <input type="datetime-local" className="mg-input" value={values.startDate} onChange={(e) => onChange('startDate', e.target.value)} />
        </MgField>
        <MgField label={t('pages.user.notice.form.endDateLabel')}>
          <input type="datetime-local" className="mg-input" value={values.endDate} onChange={(e) => onChange('endDate', e.target.value)} />
        </MgField>
      </div>
      <MgField label={t('pages.user.notice.form.pinnedLabel')}>
        <label className={`mg-check ${values.isPinned ? 'checked' : ''}`}>
          <input type="checkbox" checked={values.isPinned} onChange={(e) => onChange('isPinned', e.target.checked)} />
          <span className="mg-check-box"><span className="material-symbols-outlined">check</span></span>
          {t('pages.user.notice.form.pinnedHint')}
        </label>
      </MgField>
      <MgField label={t('pages.user.notice.form.contentLabel')} required>
        <textarea className="mg-textarea" placeholder={t('pages.user.notice.form.contentPlaceholder')} rows="8" value={values.content} onChange={(e) => onChange('content', e.target.value)} />
      </MgField>
    </>
  );

  const renderModal = ({ title, values, onChange, onClose, onSubmit, submitLabel, onDelete }) => (
    <>
      <div className="mg-modal-overlay" onClick={onClose}></div>
      <div className="mg-modal" role="dialog" aria-modal="true">
        <div className="mg-modal-head">
          <h3 className="mg-modal-title">{title}</h3>
          <button type="button" className="mg-icon-btn" onClick={onClose}>
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
        <div className="mg-modal-body">{renderNoticeForm(values, onChange)}</div>
        <div className="mg-modal-actions">
          {onDelete && (
            <button type="button" className="mg-btn danger" style={{ marginRight: 'auto' }} onClick={onDelete}>
              <span className="material-symbols-outlined">delete</span>
              {t('pages.user.notice.actions.delete')}
            </button>
          )}
          <button type="button" className="mg-btn secondary" onClick={onClose}>{t('pages.user.notice.actions.cancel')}</button>
          <button type="button" className="mg-btn primary" onClick={onSubmit}>{submitLabel}</button>
        </div>
      </div>
    </>
  );

  return (
    <MainLayout>
      <div className="mg-page">
        <ManagementHeader rootLabel={t('sidebar.user.title')} icon="manage_accounts" crumbs={[{ label: t('sidebar.user.notice') }]}>
          <button type="button" className="mg-btn primary" onClick={handleOpenAddModal}>
            <span className="material-symbols-outlined">add</span>
            {t('pages.user.notice.addBtn')}
          </button>
        </ManagementHeader>

        <div className="mg-panel fill">
          <div className="mg-toolbar">
            <label className="mg-field">
              {t('pages.user.notice.filter.category')}
              <select className="mg-select" value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
                <option value="all">{t('pages.user.notice.filter.all')}</option>
                {categoryKeys.map((k) => (
                  <option key={k} value={k}>{t(`pages.user.notice.filter.categories.${k}`)}</option>
                ))}
              </select>
            </label>
            <label className="mg-field">
              {t('pages.user.notice.filter.status')}
              <select className="mg-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="all">{t('pages.user.notice.filter.all')}</option>
                <option value="active">{t('pages.user.notice.filter.statuses.active')}</option>
                <option value="waiting">{t('pages.user.notice.filter.statuses.waiting')}</option>
                <option value="scheduled">{t('pages.user.notice.filter.statuses.scheduled')}</option>
                <option value="inactive">{t('pages.user.notice.filter.statuses.inactive')}</option>
                <option value="ended">{t('pages.user.notice.filter.statuses.ended')}</option>
              </select>
            </label>
            <div className="mg-toolbar-right">
              <div className="mg-search">
                <input
                  type="text"
                  className="mg-input"
                  placeholder={t('pages.user.notice.searchPlaceholder')}
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                />
                <span className="material-symbols-outlined">search</span>
              </div>
            </div>
          </div>

          <div className="mg-table-wrap">
            <table className="mg-table">
              <thead>
                <tr>
                  <th style={{ width: '44px' }}></th>
                  <th>{t('pages.user.notice.col.category')}</th>
                  <th>{t('pages.user.notice.col.startDate')}</th>
                  <th>{t('pages.user.notice.col.endDate')}</th>
                  <th className="left">{t('pages.user.notice.col.title')}</th>
                  <th>{t('pages.user.notice.col.author')}</th>
                  <th>{t('pages.user.notice.col.priority')}</th>
                  <th>{t('pages.user.notice.col.status')}</th>
                  <th>{t('pages.user.notice.col.views')}</th>
                  <th>{t('pages.user.notice.col.createdAt')}</th>
                  <th>{t('pages.user.notice.col.action')}</th>
                </tr>
              </thead>
              <tbody>
                {paginatedNotices.length === 0 && (
                  <tr className="mg-empty-row"><td className="mg-empty" colSpan={11}>{t('common.noData')}</td></tr>
                )}
                {paginatedNotices.map((notice) => (
                  <tr key={notice.id} onClick={() => handleNoticeClick(notice)}>
                    <td>{notice.isPinned && <span className="material-symbols-outlined mg-pin">push_pin</span>}</td>
                    <td><span className="mg-tag">{localizeCategory(notice.category)}</span></td>
                    <td>{dateCell(notice.startDate)}</td>
                    <td>{dateCell(notice.endDate)}</td>
                    <td className="left"><span className="mg-ellipsis mg-name-main" title={notice.title}>{notice.title}</span></td>
                    <td>{notice.author}</td>
                    <td>
                      <span className={`mg-badge ${PRIORITY_BADGE[notice.priorityKey] || 'warning'}`}>
                        {t(`pages.user.notice.priority.${notice.priorityKey || 'normal'}`)}
                      </span>
                    </td>
                    <td>
                      <span className={`mg-badge ${NOTICE_STATUS_BADGE[notice.statusKey] || 'neutral'}`}>
                        {t(`pages.user.notice.filter.statuses.${notice.statusKey}`)}
                      </span>
                    </td>
                    <td>
                      <span className="mg-views"><span className="material-symbols-outlined">visibility</span>{notice.views}</span>
                    </td>
                    <td>{dateCell(notice.createdAt)}</td>
                    <td>
                      <div className="mg-actions" onClick={(e) => e.stopPropagation()}>
                        <button type="button" className="mg-icon-btn danger" title={t('pages.user.notice.actions.delete')} onClick={() => handleDeleteClick(notice)}>
                          <span className="material-symbols-outlined">delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalElements > 0 && (
            <Pagination
              page={page}
              totalPages={totalPages}
              totalElements={totalElements}
              size={size}
              onPageChange={setPage}
              onSizeChange={handleSizeChange}
            />
          )}
        </div>

        {isAddModalOpen && renderModal({
          title: t('pages.user.notice.addModal.title'),
          values: newNotice,
          onChange: handleInputChange,
          onClose: handleCloseAddModal,
          onSubmit: handleAddNotice,
          submitLabel: t('pages.user.notice.actions.register')
        })}

        {isEditModalOpen && renderModal({
          title: t('pages.user.notice.editModal.title'),
          values: editNotice,
          onChange: handleEditInputChange,
          onClose: handleCloseEditModal,
          onSubmit: handleUpdateNotice,
          submitLabel: t('pages.user.notice.actions.update'),
          onDelete: () => {
            const target = notices.find((n) => n.id === editNotice.id);
            if (!target) return;
            handleCloseEditModal();
            handleDeleteClick(target);
          }
        })}
      </div>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title={t('pages.user.notice.deleteModal.title')}
        message={noticeToDelete ? t('pages.user.notice.deleteModal.message', { title: noticeToDelete.title }) : ''}
        confirmText={t('pages.user.notice.deleteModal.confirm')}
        cancelText={t('pages.user.notice.deleteModal.cancel')}
        type="danger"
      />
    </MainLayout>
  );
}

export default NoticeManagementPage;
